/**
 * Per-module config editor — modal that lets the user override any
 * config_knob declared in the module's manifest. Writes propagate
 * immediately into doc.module_config[moduleName] via setModuleConfig,
 * so the cost panel reflects each change on the next render.
 *
 * Knob type → input mapping:
 *   model_ref → text input (no model-list dropdown yet; manifest doesn't
 *               declare what values are valid beyond "anything in the
 *               LiteLLM table or a tier name")
 *   int       → number input with min/max
 *   float     → number input with step=0.01 + min/max
 *   string    → text input
 *   enum      → shadcn Select sourced from knob.enum (or .options legacy)
 *   bool      → shadcn Switch
 *   list      → comma-separated text input; serialized back as string[]
 */

import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { getCuratedModelOptions, type ModelOption } from './modelPricing';
import type { ConfigKnob, ModuleManifest } from './types';

/**
 * Group curated model options + the current value (if it's not in the
 * curated list) so the user can still see custom-authored model_ids.
 */
function buildModelOptionsForValue(currentValue: string): ModelOption[] {
  const curated = getCuratedModelOptions();
  const exists = curated.some(o => o.value === currentValue);
  if (!currentValue || exists) return curated;
  return [
    { group: 'Custom (from this pipeline)', value: currentValue },
    ...curated,
  ];
}

interface ModuleConfigEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleManifest: ModuleManifest;
  /**
   * Current overrides for this module from doc.module_config[moduleName].
   * Knobs not present here render their manifest default.
   */
  overrides: Record<string, unknown>;
  /** Write a single knob value. Pass `undefined` to clear back to default. */
  onUpdate: (
    knobName: string,
    value: string | number | boolean | string[] | null | undefined
  ) => void;
}

function knobEnumValues(knob: ConfigKnob): string[] | undefined {
  if (knob.enum && knob.enum.length > 0) return knob.enum;
  if (knob.options && knob.options.length > 0) return knob.options;
  return undefined;
}

function effectiveValue(
  knob: ConfigKnob,
  override: unknown
): string | number | boolean | string[] {
  if (override !== undefined && override !== null) {
    return override as string | number | boolean | string[];
  }
  if (knob.default === null) {
    if (knob.type === 'bool') return false;
    if (knob.type === 'list') return [];
    if (knob.type === 'int' || knob.type === 'float') return 0;
    return '';
  }
  return knob.default;
}

function isOverridden(knob: ConfigKnob, override: unknown): boolean {
  if (override === undefined) return false;
  return JSON.stringify(override) !== JSON.stringify(knob.default);
}

/**
 * Group model options into `[groupLabel, ModelOption[]]` tuples preserving
 * first-seen order. Reused by both single-select and multi-select pickers.
 */
function groupOptions(options: ModelOption[]): Array<[string, ModelOption[]]> {
  const grouped: Array<[string, ModelOption[]]> = [];
  for (const opt of options) {
    const last = grouped[grouped.length - 1];
    if (last && last[0] === opt.group) {
      last[1].push(opt);
    } else {
      grouped.push([opt.group, [opt]]);
    }
  }
  return grouped;
}

/**
 * Single model_ref picker. Renders each option as a one-line row
 * (`model · hint`) so the Radix-mirrored SelectValue fits inside the
 * 32px-tall SelectTrigger without overlapping.
 */
function SingleModelSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const options = buildModelOptionsForValue(value);
  const grouped = groupOptions(options);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-xs">
        <SelectValue placeholder="Select model" />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {grouped.map(([group, items]) => (
          <SelectGroup key={group}>
            <SelectLabel className="text-[10px] tracking-wider uppercase">
              {group}
            </SelectLabel>
            {items.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                <span className="font-mono text-xs">
                  {opt.value}
                  {opt.hint && (
                    <span className="text-chariot-text-secondary ml-2 font-sans text-[10px]">
                      {opt.hint}
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Multi-select model picker for list-of-model_ref knobs (e.g.
 * deltascan-scanner.layer2_models). Shows currently-selected models as
 * removable chips + a "+ Add" Select dropdown beneath that hides
 * already-picked entries.
 */
function MultiModelPicker({
  values,
  onChange,
}: {
  values: string[];
  onChange: (next: string[]) => void;
}) {
  // Available options exclude already-selected so the user can't pick the
  // same model twice. Re-derive on every render so the dropdown shrinks
  // as the chip row grows.
  const allOptions = getCuratedModelOptions();
  const remaining = allOptions.filter(o => !values.includes(o.value));
  const grouped = groupOptions(remaining);

  const removeAt = (idx: number) => {
    const next = values.filter((_, i) => i !== idx);
    onChange(next);
  };
  const add = (modelId: string) => {
    if (!modelId || values.includes(modelId)) return;
    onChange([...values, modelId]);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((m, idx) => (
            <span
              key={`${m}-${idx}`}
              className="bg-chariot-background-secondary text-chariot-text-primary inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono text-[10px]"
            >
              {m}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                aria-label={`Remove ${m}`}
                className="text-chariot-text-secondary hover:text-chariot-status-critical text-xs leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {remaining.length > 0 ? (
        // Empty `value` keeps the Select uncontrolled-like — picking an item
        // calls add() and the Select naturally resets to placeholder.
        <Select value="" onValueChange={add}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="+ Add model" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {grouped.map(([group, items]) => (
              <SelectGroup key={group}>
                <SelectLabel className="text-[10px] tracking-wider uppercase">
                  {group}
                </SelectLabel>
                {items.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="font-mono text-xs">
                      {opt.value}
                      {opt.hint && (
                        <span className="text-chariot-text-secondary ml-2 font-sans text-[10px]">
                          {opt.hint}
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-chariot-text-secondary text-[10px] italic">
          All available models selected.
        </p>
      )}
    </div>
  );
}

function KnobInput({
  knobName,
  knob,
  override,
  onUpdate,
}: {
  knobName: string;
  knob: ConfigKnob;
  override: unknown;
  onUpdate: ModuleConfigEditorProps['onUpdate'];
}) {
  const value = effectiveValue(knob, override);
  const enumValues = knobEnumValues(knob);

  if (knob.type === 'enum' && enumValues) {
    return (
      <Select
        value={String(value)}
        onValueChange={next => onUpdate(knobName, next)}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {enumValues.map(option => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (knob.type === 'bool') {
    return (
      <Switch
        checked={Boolean(value)}
        onCheckedChange={next => onUpdate(knobName, next)}
        aria-label={knobName}
      />
    );
  }

  if (knob.type === 'int' || knob.type === 'float') {
    return (
      <Input
        type="number"
        value={typeof value === 'number' ? value : Number(value) || 0}
        min={knob.min}
        max={knob.max}
        step={knob.type === 'float' ? 0.01 : 1}
        className="h-8 text-xs"
        onChange={e => {
          const raw = e.target.value;
          if (raw === '') {
            onUpdate(knobName, undefined);
            return;
          }
          const n = knob.type === 'float' ? parseFloat(raw) : parseInt(raw, 10);
          if (!Number.isNaN(n)) onUpdate(knobName, n);
        }}
      />
    );
  }

  if (knob.type === 'list') {
    const list = Array.isArray(value) ? value : [];
    // model_ref-typed lists get a real multi-select (chips + add-from-dropdown),
    // not a comma-separated string input — typing the routed SKU by hand is
    // too easy to mess up.
    if (knob.item_type === 'model_ref') {
      return (
        <MultiModelPicker
          values={list}
          onChange={next => onUpdate(knobName, next)}
        />
      );
    }
    return (
      <Input
        type="text"
        value={list.join(', ')}
        className="h-8 text-xs"
        placeholder="comma, separated, values"
        onChange={e => {
          const items = e.target.value
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);
          onUpdate(knobName, items);
        }}
      />
    );
  }

  if (knob.type === 'model_ref') {
    const current = typeof value === 'string' ? value : String(value ?? '');
    return (
      <SingleModelSelect
        value={current}
        onChange={next => onUpdate(knobName, next)}
      />
    );
  }

  // string falls through to plain text input.
  return (
    <Input
      type="text"
      value={typeof value === 'string' ? value : String(value ?? '')}
      className="h-8 text-xs"
      onChange={e => onUpdate(knobName, e.target.value)}
    />
  );
}

export function ModuleConfigEditor({
  open,
  onOpenChange,
  moduleManifest,
  overrides,
  onUpdate,
}: ModuleConfigEditorProps) {
  const [search, setSearch] = useState('');
  const knobs = Object.entries(moduleManifest.config_knobs);
  const filtered = search
    ? knobs.filter(
        ([name, knob]) =>
          name.toLowerCase().includes(search.toLowerCase()) ||
          knob.description?.toLowerCase().includes(search.toLowerCase())
      )
    : knobs;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{moduleManifest.display_name}</DialogTitle>
          <DialogDescription>
            Edit per-module configuration. Changes propagate to the cost
            estimate immediately. Knobs not overridden fall back to manifest
            defaults.
          </DialogDescription>
        </DialogHeader>

        {knobs.length === 0 ? (
          <p className="text-chariot-text-secondary py-6 text-center text-sm">
            This module has no configurable knobs.
          </p>
        ) : (
          <>
            {knobs.length > 6 && (
              <Input
                placeholder="Filter knobs…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="mb-2 h-8 text-xs"
              />
            )}
            <div className="max-h-[60vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,16rem)] items-start gap-x-4 gap-y-3">
                {filtered.map(([knobName, knob]) => {
                  const override = overrides[knobName];
                  const overridden = isOverridden(knob, override);
                  return (
                    <div key={knobName} className="contents">
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <Label
                          htmlFor={`knob-${knobName}`}
                          className="text-chariot-text-primary flex items-center gap-1.5 text-xs font-semibold"
                        >
                          <span className="font-mono">{knobName}</span>
                          {overridden && (
                            <button
                              type="button"
                              onClick={() => onUpdate(knobName, undefined)}
                              className="text-chariot-text-secondary hover:text-chariot-text-primary text-[10px] underline-offset-2 hover:underline"
                              title="Reset to manifest default"
                            >
                              reset
                            </button>
                          )}
                          <span className="text-chariot-text-secondary font-mono text-[10px] font-normal">
                            ({knob.type})
                          </span>
                        </Label>
                        {knob.description && (
                          <p className="text-chariot-text-secondary text-xs leading-snug">
                            {knob.description}
                          </p>
                        )}
                        <p className="text-chariot-text-secondary font-mono text-[10px]">
                          default: {JSON.stringify(knob.default)}
                        </p>
                      </div>
                      <div className="flex flex-col items-start gap-1 pt-0.5">
                        <KnobInput
                          knobName={knobName}
                          knob={knob}
                          override={override}
                          onUpdate={onUpdate}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
